/* CONTEXT:START
Wave: 1
Status: stub
Stub-Type: mock-data
Dependencies: [AdminDashboard]
Risk-Level: medium
Validation-Method: unit-test
Performance-Target: <2s
Last-Updated: 2025-08-03
CONTEXT:END */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  Assignment,
  CheckCircle,
  Edit,
  Schedule,
  Person,
  BarChart
} from '@mui/icons-material';
import { 
  PenholderMetrics, 
  PenholderAssignment 
} from '../../types';
import { mockPenholderService } from '../../services/MockPenholderService';

/**
 * Admin dashboard for viewing penholder performance metrics
 * Shows completion rates, review times, and historical performance
 */
export const PenholderMetricsDashboard: React.FC = () => {
  // State management
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [assignments, setAssignments] = useState<PenholderAssignment[]>([]);
  const [metricsData, setMetricsData] = useState<{ [key: string]: PenholderMetrics }>({});
  const [error, setError] = useState<string | null>(null);

  // Mock user data (in real implementation, would come from user service)
  const mockUsers = [
    { id: 'user-penholder-123', name: 'John Doe', email: 'john.doe@company.com' },
    { id: 'user-penholder-456', name: 'Jane Smith', email: 'jane.smith@company.com' },
    { id: 'user-penholder-789', name: 'Bob Johnson', email: 'bob.johnson@company.com' },
    { id: 'user-penholder-101', name: 'Alice Brown', email: 'alice.brown@company.com' }
  ];

  /* CONTEXT:START
  Wave: 1
  Status: stub
  Feature: Submissions Metrics - Updated admin metrics to use new structure
  Dependencies: [Updated PenholderMetrics interface]
  Risk-Level: low
  Performance-Target: <2s
  Last-Updated: 2025-08-06
  CONTEXT:END */

  // Mock metrics data for different penholders - updated for Wave 1
  const mockMetricsData: { [key: string]: PenholderMetrics } = {
    'user-penholder-123-2025-8': {
      penholderUserId: 'user-penholder-123',
      year: 2025,
      month: 8,
      submitted: 45,
      addedToReport: 12,
      pendingReview: 8,
      notChosen: 25,
      // Backward compatibility fields
      totalAssigned: 45,
      totalReviewed: 37,
      totalEdited: 5,
      averageReviewTime: 24.5
    },
    'user-penholder-456-2025-7': {
      penholderUserId: 'user-penholder-456',
      year: 2025,
      month: 7,
      submitted: 38,
      addedToReport: 15,
      pendingReview: 0,
      notChosen: 23,
      // Backward compatibility fields
      totalAssigned: 38,
      totalReviewed: 38,
      totalEdited: 6,
      averageReviewTime: 18.2
    },
    'user-penholder-789-2025-6': {
      penholderUserId: 'user-penholder-789',
      year: 2025,
      month: 6,
      submitted: 32,
      addedToReport: 8,
      pendingReview: 2,
      notChosen: 22,
      // Backward compatibility fields
      totalAssigned: 32,
      totalReviewed: 30,
      totalEdited: 3,
      averageReviewTime: 32.1
    }
  };

  // Load metrics data on mount and when year changes
  useEffect(() => {
    loadMetricsData();
  }, [selectedYear]);

  const loadMetricsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load assignment history for the selected year
      const yearAssignments = await mockPenholderService.getAssignmentHistory(selectedYear);
      setAssignments(yearAssignments);

      // Load metrics for each assignment (mock data for Wave 1)
      const metrics: { [key: string]: PenholderMetrics } = {};
      for (const assignment of yearAssignments) {
        const key = `${assignment.penholderUserId}-${assignment.year}-${assignment.month}`;
        if (mockMetricsData[key]) {
          metrics[key] = mockMetricsData[key];
        } else {
          // Generate mock metrics if not available - Wave 1 structure
          const submitted = Math.floor(Math.random() * 40) + 20; // 20-60 submissions
          const addedToReport = Math.floor(submitted * 0.3); // ~30% selected
          const notChosen = Math.floor(submitted * 0.5); // ~50% not chosen
          const pendingReview = submitted - addedToReport - notChosen; // remainder
          
          metrics[key] = {
            penholderUserId: assignment.penholderUserId,
            year: assignment.year,
            month: assignment.month,
            submitted,
            addedToReport,
            pendingReview,
            notChosen,
            // Backward compatibility fields
            totalAssigned: submitted,
            totalReviewed: addedToReport + notChosen,
            totalEdited: Math.floor(Math.random() * 4) + 1,
            averageReviewTime: Math.random() * 30 + 10
          };
        }
      }
      setMetricsData(metrics);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load metrics data');
    } finally {
      setLoading(false);
    }
  };

  const getUserName = (userId: string): string => {
    const user = mockUsers.find(u => u.id === userId);
    return user ? user.name : userId;
  };

  const getMonthName = (month: number): string => {
    return new Date(0, month - 1).toLocaleString('default', { month: 'long' });
  };

  /* CONTEXT:START
  Wave: 1
  Status: stub
  Feature: Submissions Metrics - Updated calculation functions for new metrics
  Dependencies: [Updated PenholderMetrics interface]
  Risk-Level: low
  Performance-Target: <100ms
  Last-Updated: 2025-08-06
  CONTEXT:END */

  const calculateCompletionRate = (metrics: PenholderMetrics): number => {
    // Wave 1: Use new metrics structure, fallback to old for compatibility
    const total = metrics.submitted || metrics.totalAssigned || 0;
    const reviewed = (metrics.addedToReport || 0) + (metrics.notChosen || 0) || metrics.totalReviewed || 0;
    return total > 0 ? (reviewed / total) * 100 : 0;
  };

  const calculateEditRate = (metrics: PenholderMetrics): number => {
    // Wave 1: Use new metrics structure, fallback to old for compatibility
    const reviewed = (metrics.addedToReport || 0) + (metrics.notChosen || 0) || metrics.totalReviewed || 0;
    const edited = metrics.totalEdited || 0;
    return reviewed > 0 ? (edited / reviewed) * 100 : 0;
  };

  const getPerformanceColor = (rate: number): 'success' | 'warning' | 'error' => {
    if (rate >= 80) return 'success';
    if (rate >= 60) return 'warning';
    return 'error';
  };

  const getReviewTimeColor = (hours: number): 'success' | 'warning' | 'error' => {
    if (hours <= 24) return 'success';
    if (hours <= 48) return 'warning';
    return 'error';
  };

  /* CONTEXT:START
  Wave: 1
  Status: stub
  Feature: Submissions Metrics - Updated overall statistics calculation for new metrics
  Dependencies: [Updated PenholderMetrics interface]
  Risk-Level: low
  Performance-Target: <100ms
  Last-Updated: 2025-08-06
  CONTEXT:END */

  // Calculate overall statistics - Wave 1 compatible
  const overallStats = assignments.reduce((acc, assignment) => {
    const key = `${assignment.penholderUserId}-${assignment.year}-${assignment.month}`;
    const metrics = metricsData[key];
    if (metrics) {
      // Use new metrics with fallback to old for compatibility
      const totalAssigned = metrics.submitted || metrics.totalAssigned || 0;
      const totalReviewed = (metrics.addedToReport || 0) + (metrics.notChosen || 0) || metrics.totalReviewed || 0;
      const totalEdited = metrics.totalEdited || 0;
      const averageReviewTime = metrics.averageReviewTime || 0;
      
      acc.totalAssigned += totalAssigned;
      acc.totalReviewed += totalReviewed;
      acc.totalEdited += totalEdited;
      acc.totalReviewTime += averageReviewTime * totalReviewed;
      acc.count += 1;
    }
    return acc;
  }, { totalAssigned: 0, totalReviewed: 0, totalEdited: 0, totalReviewTime: 0, count: 0 });

  const overallCompletionRate = overallStats.totalAssigned > 0 
    ? (overallStats.totalReviewed / overallStats.totalAssigned) * 100 
    : 0;
  const overallEditRate = overallStats.totalReviewed > 0 
    ? (overallStats.totalEdited / overallStats.totalReviewed) * 100 
    : 0;
  const overallAverageReviewTime = overallStats.totalReviewed > 0 
    ? overallStats.totalReviewTime / overallStats.totalReviewed 
    : 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading metrics data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Penholder Metrics Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Performance analytics and review statistics
          </Typography>
        </Box>
        
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            label="Year"
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            <MenuItem value={2024}>2024</MenuItem>
            <MenuItem value={2025}>2025</MenuItem>
            <MenuItem value={2026}>2026</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Overall Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Assignment color="primary" />
                <Typography color="text.secondary" variant="subtitle2">
                  Total Assigned
                </Typography>
              </Box>
              <Typography variant="h4">
                {overallStats.totalAssigned}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircle color="success" />
                <Typography color="text.secondary" variant="subtitle2">
                  Completion Rate
                </Typography>
              </Box>
              <Typography variant="h4">
                {overallCompletionRate.toFixed(0)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={overallCompletionRate}
                color={getPerformanceColor(overallCompletionRate)}
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Edit color="secondary" />
                <Typography color="text.secondary" variant="subtitle2">
                  Edit Rate
                </Typography>
              </Box>
              <Typography variant="h4">
                {overallEditRate.toFixed(0)}%
              </Typography>
              <LinearProgress
                variant="determinate"
                value={overallEditRate}
                color="secondary"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Schedule color="warning" />
                <Typography color="text.secondary" variant="subtitle2">
                  Avg Review Time
                </Typography>
              </Box>
              <Typography variant="h4">
                {overallAverageReviewTime.toFixed(1)}h
              </Typography>
              <Chip
                label={overallAverageReviewTime <= 24 ? 'Excellent' : overallAverageReviewTime <= 48 ? 'Good' : 'Needs Improvement'}
                color={getReviewTimeColor(overallAverageReviewTime)}
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Detailed Metrics Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Monthly Performance Details
          </Typography>
          
          {assignments.length === 0 ? (
            <Alert severity="info">
              No penholder assignments found for {selectedYear}
            </Alert>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell>Penholder</TableCell>
                    <TableCell align="center">Assigned</TableCell>
                    <TableCell align="center">Reviewed</TableCell>
                    <TableCell align="center">Edited</TableCell>
                    <TableCell align="center">Completion Rate</TableCell>
                    <TableCell align="center">Edit Rate</TableCell>
                    <TableCell align="center">Avg Review Time</TableCell>
                    <TableCell align="center">Performance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignments.map((assignment) => {
                    const key = `${assignment.penholderUserId}-${assignment.year}-${assignment.month}`;
                    const metrics = metricsData[key];
                    
                    if (!metrics) return null;
                    
                    const completionRate = calculateCompletionRate(metrics);
                    const editRate = calculateEditRate(metrics);
                    
                    return (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {getMonthName(assignment.month)} {assignment.year}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <Person fontSize="small" color="action" />
                            <Typography variant="body2">
                              {getUserName(assignment.penholderUserId)}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {metrics.totalAssigned}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {metrics.totalReviewed}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {metrics.totalEdited}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                            <Typography variant="body2">
                              {completionRate.toFixed(0)}%
                            </Typography>
                            <LinearProgress
                              variant="determinate"
                              value={completionRate}
                              color={getPerformanceColor(completionRate)}
                              sx={{ width: 60, height: 4 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {editRate.toFixed(0)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2">
                            {(metrics.averageReviewTime || 0).toFixed(1)}h
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={
                              completionRate >= 80 && (metrics.averageReviewTime || 0) <= 24 
                                ? 'Excellent'
                                : completionRate >= 60 && (metrics.averageReviewTime || 0) <= 48
                                ? 'Good'
                                : 'Needs Improvement'
                            }
                            color={
                              completionRate >= 80 && (metrics.averageReviewTime || 0) <= 24 
                                ? 'success'
                                : completionRate >= 60 && (metrics.averageReviewTime || 0) <= 48
                                ? 'warning'
                                : 'error'
                            }
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};
